import React, { useState } from 'react';
import Icon from '../../components/Icon';

/**
 * League of Record (Pro Analyst) By-Laws.
 *
 * Static governance document. Content reconciled against the authoritative
 * Sleeper league settings (league_id 1343744221349158912) on 2026-08-05.
 * Where the prior draft (v3.1) disagreed with Sleeper on a mechanical setting,
 * Sleeper wins and the change is noted in the reconciliation log at the bottom.
 */

const ARTICLES = [
  {
    id: 'art-1',
    num: 'I',
    title: 'League Structure',
    body: (
      <>
        <h4>1. League Name</h4>
        <p>The name of this football league is <strong>Pro Analyst</strong> (the League of Record redraft).</p>

        <h4>2. Team Count</h4>
        <p>Pro Analyst shall have <strong>ten (10) teams</strong> managed by ten individual managers.</p>

        <h4>3. Platform</h4>
        <ul>
          <li>The <strong>Sleeper</strong> app manages the league.</li>
          <li><strong>Messenger</strong> is used for official communications.</li>
          <li><strong>LeagueSafe</strong> is used to track all league dues.</li>
        </ul>

        <h4>4. Communications &amp; Conduct</h4>
        <ul>
          <li>For league-specific issues, contact the commissioner first.</li>
          <li>If the commissioner is unreachable and the issue is time-sensitive (e.g. a roster change you can't make yourself), use Messenger so the time of the request can be verified and the change made retroactively if necessary.</li>
          <li><strong>Respectful communication:</strong> friendly trash talk is part of the fun, but personal attacks, derogatory remarks, and bullying will not be tolerated.</li>
          <li><strong>Active participation:</strong> managers are expected to actively manage their teams all season — setting lineups, adding/dropping, and responding to trade proposals promptly — even when playoff chances are slim.</li>
          <li><strong>Disputes:</strong> handle disagreements maturely; if unresolved, the commissioner may mediate or make a final decision in the league's best interest.</li>
          <li><strong>Consequences:</strong> breaches may result in warnings, penalties (such as loss of draft position), or, in severe cases, expulsion. The commissioner determines and imposes the appropriate consequence without requiring a league vote or approval.</li>
        </ul>

        <h4>5. Commissioner</h4>
        <p><strong>Active Commissioner:</strong> Chesley McNeil.</p>
        <p><strong>By-law enforcement authority:</strong> the commissioner has sole authority to interpret, administer, and enforce these by-laws and may take any action reasonably necessary to uphold an existing by-law without a league vote, approval, or ratification. This authority does not permit the commissioner to create or amend a by-law outside the processes in Articles VI and VII.</p>
        <p><strong>Replacing the commissioner:</strong></p>
        <ol>
          <li>A member proposes replacement for a valid reason (inactivity, perceived bias, etc.), communicated to the league.</li>
          <li>A <strong>72-hour discussion period</strong> follows; the commissioner may defend their actions.</li>
          <li>A formal vote is held. A <strong>supermajority (75%+)</strong> is required to approve replacement; the commissioner in question abstains.</li>
          <li>If it passes, new commissioners are nominated (self- or peer-nominated) and may state their case.</li>
          <li>The league elects a new commissioner by majority; a runoff between the top two is held if no majority.</li>
          <li>A <strong>one-week transition</strong> transfers access and responsibilities.</li>
          <li>Rules and procedures are reviewed, and every step is communicated transparently.</li>
        </ol>
      </>
    ),
  },
  {
    id: 'art-2',
    num: 'II',
    title: 'League Entry, Fees & Payout',
    body: (
      <>
        <h4>1. Entry Fee</h4>
        <ul>
          <li>The league entry fee is a flat <strong>$75.00</strong>, paid by the commissioner's deadline before the season.</li>
          <li>
            <strong>Late dues → draft penalty:</strong> an unpaid member forfeits their 1st-round pick. If allowed to remain, they cannot trade drafted players until paid. Members who pay during or immediately after the draft may appeal. If dues remain unpaid two weeks after the draft, additional penalties may apply (point deductions, restricted free-agent acquisitions, or expulsion).
          </li>
          <li><strong>Winner's banner:</strong> an additional amount is collected annually to cover the champion's banner, set by the commissioner and communicated before the dues deadline.</li>
        </ul>

        <h4>2. Miscellaneous Fees</h4>
        <p>Additional fees for draft hosting or prizes may be assigned as needed and split among all ten managers.</p>

        <h4>3. Payout</h4>
        <ul>
          <li><strong>1st Place</strong> — $450.00 (60%)</li>
          <li><strong>2nd Place</strong> — $225.00 (30%)</li>
          <li><strong>3rd Place</strong> — $75.00 (10%)</li>
        </ul>
      </>
    ),
  },
  {
    id: 'art-3',
    num: 'III',
    title: 'Draft Order & Style',
    body: (
      <>
        <p>Pro Analyst drafts <strong>snake style with a 3rd-round reversal</strong> (made permanent in 2024).</p>
        <p>Each year, draft position is set by a lottery-points system based on the prior season's playoff results:</p>
        <div className="bylaws-two-col">
          <div>
            <h4>Loser Bracket</h4>
            <ul>
              <li>5th place — 10 pts</li>
              <li>6th place — 9 pts</li>
              <li>7th place — 8 pts</li>
              <li>8th place — 7 pts</li>
              <li>9th place — 6 pts</li>
              <li>10th place — 5 pts</li>
            </ul>
          </div>
          <div>
            <h4>Championship Bracket</h4>
            <ul>
              <li>1st place — 1 pt</li>
              <li>2nd place — 2 pts</li>
              <li>3rd place — 3 pts</li>
              <li>4th place — 4 pts</li>
            </ul>
          </div>
        </div>
        <p>Any new team joining the league is slotted into the 1st draft position, and existing positions adjust accordingly.</p>
      </>
    ),
  },
  {
    id: 'art-4',
    num: 'IV',
    title: 'Roster & Starting Lineup',
    sleeper: true,
    body: (
      <>
        <p className="bylaws-note bylaws-note--sleeper">
          <Icon emoji="⚙️" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Roster, lineup, waiver, and scoring settings are governed by Sleeper and mirrored here.
        </p>

        <h4>1. Starting Lineup (12 starters)</h4>
        <div className="bylaws-lineup">
          <span>QB &times; 1</span>
          <span>RB &times; 2</span>
          <span>WR &times; 3</span>
          <span>TE &times; 1</span>
          <span>FLEX (RB/WR/TE) &times; 2</span>
          <span>SUPERFLEX (QB/RB/WR/TE) &times; 1</span>
          <span>K &times; 1</span>
          <span>D/ST &times; 1</span>
        </div>

        <h4>2. Bench</h4>
        <p><strong>8 bench spots.</strong></p>

        <h4>3. Injured Reserve (IR)</h4>
        <ul>
          <li><strong>3 IR spots.</strong></li>
          <li>Eligibility: players designated <strong>IR</strong>, <strong>Out</strong>, or <strong>COVID</strong> by their NFL team.</li>
          <li>Players must be moved out of IR once they are no longer designated.</li>
        </ul>

        <h4>4. Free-Agent Acquisition Budget (FAAB)</h4>
        <ul>
          <li>Each team receives a <strong>$100</strong> season-long budget that does not refresh.</li>
          <li>Highest bid at the end of the waiver period wins the player; the amount is deducted from FAAB.</li>
          <li>Ties go to the team with the lower waiver order.</li>
          <li>Waivers process <strong>Wednesday</strong> (2-day claim window); unclaimed players become first-come free agents until their game starts.</li>
          <li>Leftover FAAB does not carry over and has no other value.</li>
        </ul>

        <h4>5. Keepers</h4>
        <ul>
          <li>Each team may keep <strong>two (2) players</strong> from the previous season ("Keepers").</li>
          <li>Keepers must be declared to the commissioner <strong>no later than two weeks (14 days) before the draft</strong>. Once the deadline passes, each undeclared keeper slot is forfeited for that season. A manager may not add, retain, replace, or otherwise designate a keeper for a forfeited slot after the deadline.</li>
          <li>
            <strong>Round penalty:</strong> beginning with the 2024 draft, kept players ("Incumbents") were slotted in the 3rd round. Each subsequent year a player is kept, the penalty rises one round (e.g. 3rd → 2nd → 1st). Once a player would be kept as a 1st-round pick, they cannot be kept again the following year. Other drafted players kept in later years incur a one-round penalty per year based on their original draft round.
          </li>
          <li>Locked keepers may only be traded during the season; after the season, a kept player can only be traded once keepers lock for the following year.</li>
        </ul>

        <h4>6. Special Scoring</h4>
        <ul>
          <li>The <strong>#1 playoff seed</strong> receives a home-field advantage of <strong>+5 pts</strong>.</li>
          <li><strong>TE Premium:</strong> +0.5 per TE reception (TEs score 1.5 PPR).</li>
          <li><strong>First-down bonus:</strong> +0.3 for each rushing and receiving first down, applied league-wide all season.</li>
          <li>Baseline scoring (per Sleeper): full PPR (1.0/reception), passing TD 6 pts, 1 pt per 25 passing yards, 1 pt per 10 rushing/receiving yards, standard kicker and D/ST scoring. All remaining scoring detail lives in Sleeper.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'art-5',
    num: 'V',
    title: 'Trade Rules & Regulations',
    sleeper: true,
    body: (
      <>
        <h4>1. Proposals</h4>
        <p>Any team may propose a trade. Both teams must agree before submission. Once submitted and confirmed by both parties, the trade is final unless vetoed by the commissioner.</p>

        <h4>2. Processing &amp; Veto</h4>
        <p className="bylaws-note bylaws-note--sleeper">
          <Icon emoji="⚙️" size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          In Sleeper, accepted trades <strong>process immediately</strong> (no automatic review delay). A trade may still be reversed by <strong>commissioner veto</strong> or by a <strong>league veto of 5 of 10 managers</strong>. Commissioner veto power is used sparingly and transparently.
        </p>

        <h4>3. Trade Deadlines</h4>
        <ul>
          <li>Player trades close <strong>after Week 11</strong>.</li>
          <li>Draft picks may not be traded after <strong>Week 5</strong>; pick trades submitted after Week 5 games conclude are automatically rejected. Picks may only be traded in-season.</li>
        </ul>

        <h4>4. Fair Play &amp; Collusion</h4>
        <p>Trades must be made in good faith. Collusion to unfairly benefit one team is prohibited and may lead to penalties, up to expulsion, at the discretion of the commissioner.</p>

        <h4>5. Reversals &amp; Conduct</h4>
        <ul>
          <li>Trades may only be reversed if both parties agree and notify the commissioner before finalization; after 48 hours a completed trade will not be reversed.</li>
          <li>Open negotiation is encouraged, but pressure or harassment to accept a deal is not tolerated.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'art-6',
    num: 'VI',
    title: 'Creating New League Rules',
    body: (
      <>
        <p>When a dispute is not covered by these by-laws, the league discusses possible resolutions. The commissioner then creates a poll (in Messenger) and the league follows the course with a plurality of votes. A new rule is added so similar disputes resolve the same way.</p>
        <h4>1. End of Season</h4>
        <ul>
          <li>Rule discussions are raised after the season.</li>
          <li>Changes pass only if more than half the league (quorum) votes to approve.</li>
          <li>An approved resolution can only be revisited the following season.</li>
          <li>There are two windows for rule/structure amendments: once at the end of the season, and once at fantasy kickoff in July before the season.</li>
        </ul>
        <h4>2. Emergency Rule Changes</h4>
        <p>The commissioner may invoke an emergency rule change if league circumstances change; the same approval process applies.</p>
      </>
    ),
  },
  {
    id: 'art-7',
    num: 'VII',
    title: 'Amendments',
    body: (
      <>
        <p>Amendments govern overall changes to this document (distinct from adding routine rules under Article VI).</p>
        <ol>
          <li><strong>Proposal:</strong> any member may propose an amendment in writing, with a detailed explanation and rationale.</li>
          <li><strong>Notification &amp; discussion:</strong> the commissioner notifies the league; a one-week discussion period follows for questions and suggested modifications.</li>
          <li>
            <strong>Approval (two steps):</strong>
            <ul>
              <li>Initial approval requires a <strong>two-thirds majority</strong>.</li>
              <li>Ratification requires a second vote of <strong>three-fourths</strong> to be officially added.</li>
            </ul>
          </li>
          <li><strong>Refinement:</strong> if it fails, the proposer may revise and resubmit.</li>
          <li><strong>Documentation:</strong> once ratified, the amendment is documented and the updated by-laws are redistributed and posted to league platforms.</li>
          <li><strong>Periodic review:</strong> the by-laws are reviewed periodically (e.g. annually) to keep them current.</li>
        </ol>
      </>
    ),
  },
  {
    id: 'art-8',
    num: 'VIII',
    title: 'Ratification of Final By-Laws',
    body: (
      <>
        <ol>
          <li><strong>Draft presentation:</strong> the commissioner presents a draft to all members via email or a league platform.</li>
          <li><strong>Review period:</strong> members have <strong>48 hours</strong> to review thoroughly.</li>
          <li><strong>Feedback:</strong> members submit feedback and amendment proposals in writing to a central place.</li>
          <li><strong>Compilation:</strong> the commissioner compiles and categorizes feedback.</li>
          <li><strong>Revision:</strong> the by-laws are revised and a final draft is shared.</li>
          <li><strong>Final vote:</strong> members vote to approve (simple majority).</li>
          <li><strong>Documentation:</strong> approved by-laws are formally documented and stored in an accessible location.</li>
          <li><strong>Implementation:</strong> upon approval, the by-laws become the official governing document for all league actions and disputes.</li>
          <li><strong>Periodic review:</strong> conducted periodically to keep the by-laws relevant, following the amendment process.</li>
        </ol>
      </>
    ),
  },
  {
    id: 'art-9',
    num: 'IX',
    title: 'Conflict Resolution',
    body: (
      <>
        <p>The commissioner has final authority to interpret, administer, and enforce these by-laws. No league vote, approval, or ratification is required for the commissioner to uphold or enforce an existing by-law. League votes remain required only where these by-laws expressly require them, including the creation or amendment of rules under Articles VI and VII.</p>
        <h4>1. General Process</h4>
        <ol>
          <li><strong>Direct resolution:</strong> members first attempt to resolve the issue directly and respectfully.</li>
          <li><strong>Commissioner ruling:</strong> if unresolved, the commissioner interprets the applicable by-laws and issues a binding ruling.</li>
        </ol>
        <h4>2. Conflicts Involving the Commissioner</h4>
        <p>If the commissioner is involved or has a perceived conflict of interest, <strong>Joseph Cole</strong> serves as an impartial mediator and may present a non-binding recommendation. This process does not require a league vote and does not limit the commissioner's final authority to interpret and enforce existing by-laws. Any proposal to replace the commissioner must follow Article I §5.</p>
        <p>All members are encouraged to handle disputes maturely and with the league's best interests at heart.</p>
      </>
    ),
  },
  {
    id: 'art-10-proposed',
    num: 'X',
    title: 'Proposed Member Removal Procedure',
    proposed: true,
    body: (
      <>
        <div className="bylaws-proposal-status">
          <strong>Proposed for the 2027 season · Pending league approval</strong>
          <span>This article is published for offseason review only. It is not enforceable during the 2026 season and becomes effective no earlier than the start of the 2027 league year after approval through Articles VI and VII.</span>
        </div>

        <h4>1. Removal Is a Last Resort</h4>
        <p>Permanent removal is reserved for conduct that materially threatens league operation, fairness, safety, or continuity. When correction remains reasonably possible, the league should use a warning, corrective plan, probation, or temporary co-manager before permanent removal.</p>

        <h4>2. Grounds for Review</h4>
        <p>A removal case may proceed only when the written request alleges at least one of the following and identifies supporting evidence:</p>
        <ul>
          <li>Sustained failure to manage a franchise after documented notice and a reasonable opportunity to correct the behavior.</li>
          <li>Collusion, intentional roster dumping, concealed side compensation, or deliberate circumvention of league rules.</li>
          <li>Repeated harassment, threats, discriminatory abuse, or other serious conduct that makes continued league participation unsafe or unworkable.</li>
          <li>Failure to pay required league dues after the published deadline, written notice, and any applicable cure period.</li>
          <li>Deliberate falsification of league evidence, tampering with league systems, or misuse of commissioner or co-manager access.</li>
        </ul>
        <p className="bylaws-note bylaws-note--proposal"><strong>Not grounds by themselves:</strong> losing, rebuilding for a future season, unconventional strategy, low transaction volume, rejecting trades, an unpopular or uneven trade, an active player scoring zero, projection disagreement, personality conflict, or criticism of league leadership.</p>

        <h4>3. Who May Request Review</h4>
        <p>Any active league member or the commissioner may submit a written removal-review request. It must identify the manager, controlling rule, dates or scoring weeks involved, requested outcome, and evidence believed to support review. A request is an allegation—not proof, a violation, or a removal vote.</p>

        <h4>4. Initial Screening</h4>
        <ul>
          <li>The commissioner determines within 72 hours whether the request states a recognized ground and includes enough verifiable information to investigate.</li>
          <li>Unsupported, retaliatory, repetitive, or purely subjective requests are dismissed in writing.</li>
          <li>If the commissioner is the subject, petitioner, material witness, or otherwise conflicted, an uninvolved manager selected by majority vote administers the case.</li>
        </ul>

        <h4>5. Evidence Packet</h4>
        <p>A case that passes screening receives a dated evidence packet containing the allegation, applicable rules, Sleeper records, relevant timestamped communications, prior notices or corrective opportunities, the manager's response, meaningful counter-evidence, and known data limitations. Rumor, anonymous accusation without corroboration, projections, and subjective roster-value opinions are not sufficient evidence.</p>
        <p>The decision standard is <strong>clear and convincing league evidence</strong>: reliable, documented information that makes the alleged ground highly probable. The burden remains on the party requesting removal.</p>

        <h4>6. Notice, Response &amp; Discussion</h4>
        <ol>
          <li>The affected manager receives the complete league-relevant evidence and written notice of the possible outcome.</li>
          <li>The manager has <strong>seven calendar days</strong> to respond, correct factual errors, provide counter-evidence, or propose a corrective plan.</li>
          <li>After the response window, the evidence packet and response are shared with eligible voters for a <strong>72-hour discussion period</strong>.</li>
          <li>Unrelated private information is redacted. A manager's silence may be documented but does not relieve voters of the evidence standard.</li>
        </ol>

        <h4>7. Interim Protection</h4>
        <p>Before a final vote, the commissioner or neutral administrator may impose only the least restrictive temporary measure needed to protect the league, such as suspending trades or assigning lineup-only assistance. No permanent player drop, keeper reassignment, draft-position transfer, or roster liquidation may occur.</p>

        <h4>8. Removal Vote</h4>
        <ul>
          <li>The affected manager does not vote.</li>
          <li>Removal requires approval from <strong>75% of all eligible, non-recused managers, rounded up</strong>. Abstentions do not reduce the number of affirmative votes required.</li>
          <li>Each vote and the final count are recorded. Ballot secrecy may be used, but the result, threshold, and controlling findings must be published.</li>
          <li>If removal fails, the same allegations may not be resubmitted without material new evidence or a new incident.</li>
        </ul>

        <h4>9. Limited Reconsideration</h4>
        <p>Within 72 hours of the result, the affected manager may request reconsideration based only on material new evidence or a specific procedural error. If a majority of eligible voters grants reconsideration, the evidence is corrected and a new vote uses the same 75% threshold.</p>

        <h4>10. Effect of Removal</h4>
        <p>Upon approved removal, the commissioner begins a documented replacement search. The roster and keeper rights remain attached to the existing franchise and may not be redistributed. A replacement owner inherits that franchise and does not receive the first-draft-position benefit reserved by Article III for a genuinely new expansion team. Financial obligations or refunds follow Article II.</p>

        <h4>11. Relationship to Existing Rules</h4>
        <p>If approved, this article supersedes conflicting clauses that permit discretionary permanent expulsion of an ordinary member without a vote. The commissioner retains authority to enforce other by-laws and impose proportionate non-removal remedies. Replacing the commissioner as commissioner remains governed by Article I §5; removing that person's franchise membership follows this article.</p>
      </>
    ),
  },
];

const CHANGELOG = [
  { date: '2026-08-05', text: 'Reconciled with Sleeper: IR spots corrected 2 → 3; keepers standardized to 2 (removed a stale "1 keeper" clause); first-down bonus clarified as +0.3 rush & receiving league-wide all season (previously "first round only"); trade processing/veto updated to reflect Sleeper (immediate processing, 5-vote league veto).' },
  { date: '2024-01-18', text: 'Article V §4 — draft picks may not be traded after Week 5; late pick trades auto-rejected; picks tradable in-season only.' },
  { date: '2023-09-07', text: 'Article IV §5 (Keepers) — locked keepers tradable in-season; verbiage tightened.' },
];

export default function LORBylaws() {
  const [openLog, setOpenLog] = useState(false);

  return (
    <div className="lor-bylaws-page">
      <style>{BYLAWS_CSS}</style>

      <div className="lor-page-header">
        <h2 className="lor-page-title">📜 League By-Laws</h2>
        <div className="lor-page-meta">Pro Analyst · League of Record · Ratified Governing Document</div>
      </div>

      <div className="lor-card bylaws-hero">
        <p>
          These are the official by-laws of the Pro Analyst redraft league (the League of Record).
          Roster, lineup, waiver, and scoring rules are governed by <strong>Sleeper</strong> and mirrored
          here; where a prior draft disagreed with Sleeper, Sleeper is authoritative. This page was last
          reconciled with the live Sleeper settings on <strong>August 5, 2026</strong>.
        </p>
      </div>

      <nav className="bylaws-toc lor-card">
        <div className="bylaws-toc-title">Articles</div>
        <div className="bylaws-toc-links">
          {ARTICLES.map(a => (
            <a key={a.id} href={`#${a.id}`} className="bylaws-toc-link">
              <span className="bylaws-toc-num">{a.num}</span>{a.title}
            </a>
          ))}
        </div>
      </nav>

      {ARTICLES.map(a => (
        <section key={a.id} id={a.id} className="lor-card bylaws-article">
          <h3 className="bylaws-article-title">
            <span className="bylaws-article-num">Article {a.num}</span>
            {a.title}
            {a.sleeper && <span className="bylaws-sleeper-tag">Sleeper-governed</span>}
            {a.proposed && <span className="bylaws-proposed-tag">2027 proposal</span>}
          </h3>
          <div className="bylaws-body">{a.body}</div>
        </section>
      ))}

      <section className="lor-card bylaws-article">
        <button className="bylaws-log-toggle" onClick={() => setOpenLog(o => !o)}>
          {openLog ? '▲' : '▼'} Summary of Changes
        </button>
        {openLog && (
          <ul className="bylaws-changelog">
            {CHANGELOG.map((c, i) => (
              <li key={i}>
                <span className="bylaws-changelog-date">{c.date}</span>
                <span>{c.text}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const BYLAWS_CSS = `
.lor-bylaws-page { max-width: 900px; margin: 0 auto; }
.bylaws-hero p { margin: 0; color: var(--text2); line-height: 1.6; font-size: 0.95rem; }
.bylaws-toc { margin: 16px 0; }
.bylaws-toc-title {
  font-family: var(--font-mono); text-transform: uppercase; letter-spacing: 1px;
  font-size: 0.72rem; color: var(--text3); margin-bottom: 10px;
}
.bylaws-toc-links { display: flex; flex-wrap: wrap; gap: 8px; }
.bylaws-toc-link {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 7px 12px; border-radius: 8px; text-decoration: none;
  background: rgba(var(--lor-accent-rgb), 0.06);
  border: 1px solid rgba(var(--lor-accent-rgb), 0.15);
  color: var(--text2); font-size: 0.85rem; transition: all 0.15s;
}
.bylaws-toc-link:hover { background: rgba(var(--lor-accent-rgb), 0.14); color: var(--text); }
.bylaws-toc-num {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 4px; border-radius: 5px;
  background: rgba(var(--lor-accent-rgb), 0.2); color: var(--lor-accent);
  font-family: var(--font-mono); font-size: 0.7rem; font-weight: 700;
}
.bylaws-article { margin: 16px 0; scroll-margin-top: 20px; }
.bylaws-article-title {
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
  font-family: var(--font-display); color: var(--text);
  font-size: 1.15rem; margin: 0 0 14px 0;
  padding-bottom: 12px; border-bottom: 1px solid var(--border);
}
.bylaws-article-num {
  font-family: var(--font-mono); font-size: 0.72rem; letter-spacing: 1px;
  text-transform: uppercase; color: var(--lor-accent);
  background: rgba(var(--lor-accent-rgb), 0.1); border: 1px solid rgba(var(--lor-accent-rgb), 0.2);
  padding: 4px 8px; border-radius: 6px;
}
.bylaws-sleeper-tag {
  margin-left: auto; font-family: var(--font-mono); font-size: 0.62rem;
  text-transform: uppercase; letter-spacing: 0.5px; color: var(--text3);
  border: 1px solid var(--border); padding: 3px 7px; border-radius: 5px;
}
.bylaws-proposed-tag {
  margin-left: auto; font-family: var(--font-mono); font-size: 0.62rem;
  text-transform: uppercase; letter-spacing: 0.5px; color: var(--yellow);
  border: 1px solid rgba(255, 211, 42, 0.3); background: rgba(255, 211, 42, 0.06);
  padding: 3px 7px; border-radius: 5px;
}
.bylaws-body { color: var(--text2); line-height: 1.6; font-size: 0.92rem; }
.bylaws-body h4 {
  color: var(--text); font-size: 0.95rem; margin: 18px 0 8px 0; font-weight: 600;
}
.bylaws-body h4:first-child { margin-top: 0; }
.bylaws-body p { margin: 8px 0; }
.bylaws-body ul, .bylaws-body ol { margin: 8px 0; padding-left: 22px; }
.bylaws-body li { margin: 6px 0; }
.bylaws-body strong { color: var(--text); }
.bylaws-note {
  border-radius: 8px; padding: 10px 12px; font-size: 0.86rem; margin: 10px 0;
}
.bylaws-note--sleeper {
  background: rgba(var(--lor-accent-rgb), 0.07);
  border: 1px solid rgba(var(--lor-accent-rgb), 0.18);
  color: var(--text2);
}
.bylaws-note--proposal {
  background: rgba(255, 211, 42, 0.055);
  border: 1px solid rgba(255, 211, 42, 0.22);
  color: var(--text2);
}
.bylaws-proposal-status {
  display: grid; gap: 6px; margin-bottom: 18px; padding: 14px;
  border: 1px solid rgba(255, 211, 42, 0.28); border-left: 4px solid var(--yellow);
  border-radius: 9px; background: rgba(255, 211, 42, 0.055);
}
.bylaws-proposal-status strong {
  color: var(--yellow); font: 0.72rem var(--font-mono); letter-spacing: 0.7px; text-transform: uppercase;
}
.bylaws-proposal-status span { color: var(--text2); font-size: 0.86rem; line-height: 1.55; }
.bylaws-two-col { display: flex; flex-wrap: wrap; gap: 24px; }
.bylaws-two-col > div { flex: 1; min-width: 180px; }
.bylaws-lineup { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0; }
.bylaws-lineup span {
  font-family: var(--font-mono); font-size: 0.8rem; color: var(--text);
  background: rgba(var(--lor-accent-rgb), 0.08);
  border: 1px solid rgba(var(--lor-accent-rgb), 0.18);
  padding: 6px 10px; border-radius: 7px;
}
.bylaws-log-toggle {
  background: none; border: none; cursor: pointer; padding: 0;
  font-family: var(--font-display); font-size: 1.05rem; color: var(--text);
}
.bylaws-changelog { list-style: none; margin: 14px 0 0 0; padding: 0; }
.bylaws-changelog li {
  display: flex; gap: 12px; padding: 10px 0; border-top: 1px solid var(--border);
  color: var(--text2); font-size: 0.86rem; line-height: 1.5;
}
.bylaws-changelog-date {
  font-family: var(--font-mono); font-size: 0.72rem; color: var(--lor-accent);
  white-space: nowrap; padding-top: 1px;
}
@media (max-width: 640px) {
  .bylaws-article-title { font-size: 1.02rem; }
  .bylaws-sleeper-tag { margin-left: 0; }
  .bylaws-proposed-tag { margin-left: 0; }
}
`;
